import { useContext, useEffect, useState } from "react";
import { AppContext } from "../../App";
import { getInlineLoader } from "../../Helpers";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  FaArrowRight,
  FaClock,
  FaShieldAlt,
  FaMicrochip,
  FaCheckCircle
} from "react-icons/fa";

export default function SystemsAbout() {
  const { navTo } = useContext(AppContext);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(true);
    AOS.init({
      duration: 1000,
      easing: "ease-out-expo",
      once: true,
      offset: 50
    });
  }, []);

  if (!ready) return <div className="container mSupportLoading">{getInlineLoader()}</div>;

  const includedFeatures = [
    "Business logic & requirements architecture",
    "High-fidelity UI/UX prototyping",
    "Full-stack development & SSID integration",
    "Rigorous QA & institutional user training",
    "Production-grade deployment & support"
  ];

  return (
    <section className="pricing-section position-relative overflow-hidden py-5" style={{ 
      background: '#ffffff',
      minHeight: '90vh'
    }}>
      
      {/* Structural Background Accents */}
      <div className="position-absolute top-0 start-0 w-100 h-100" style={{ 
        zIndex: 0, 
        pointerEvents: 'none',
        backgroundImage: `linear-gradient(rgba(244,145,30,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(244,145,30,0.03) 1px, transparent 1px)`,
        backgroundSize: '100px 100px'
      }}></div>

      <div className="container position-relative" style={{ zIndex: 1, maxWidth: '1100px' }}>
        
        {/* Header: Centered Minimalist */}
        <div className="row justify-content-center mb-5 pt-4">
          <div className="col-lg-8 text-center">
            <div className="d-inline-flex align-items-center gap-2 mb-3 px-3 py-1 rounded-1" style={{ background: 'rgba(26,26,46,0.05)', color: 'var(--primaryColor)', fontSize: '0.75rem', fontWeight: '700', letterSpacing: '1.5px' }}>
              <FaMicrochip size={12}/> SYSTEM ARCHITECTURE
            </div>
            <h2 className="font-bold mb-4" data-aos="fade-up" style={{ 
              fontSize: 'clamp(2.2rem, 5vw, 3.2rem)', 
              color: 'var(--primaryColor)',
              lineHeight: 1.1,
              letterSpacing: '-0.03em'
            }}>
              Investing in <span style={{ color: 'var(--secondaryColor)' }}>Scalable Efficiency</span>
            </h2>
            <p className="mx-auto text-muted" data-aos="fade-up" data-aos-delay="100" style={{ fontSize: '1.05rem', maxWidth: '600px' }}>
              We don't sell templates. We engineer custom business logic ecosystems designed to automate your most complex workflows.
            </p>
          </div>
        </div>

        {/* Pricing Architecture Card */}
        <div className="row justify-content-center" data-aos="fade-up" data-aos-delay="200">
          <div className="col-lg-12">
            <div className="pricing-card rounded-1 p-4 p-lg-5 position-relative border-0 shadow-lg" style={{
              background: '#12121c',
              overflow: 'hidden'
            }}>
              {/* Top Accent Strip */}
              <div className="position-absolute top-0 start-0 w-100" style={{ height: '5px', background: 'var(--secondaryColor)' }}></div>

              <div className="row g-5 align-items-center">
                
                {/* Left Side: Financials */}
                <div className="col-lg-5 text-white">
                  <div className="mb-4">
                    <span className="text-uppercase opacity-50 fw-bold small tracking-widest">Base Investment</span>
                    <div className="d-flex align-items-baseline mt-2">
                      <h3 className="fw-bold mb-0" style={{ fontSize: '3.5rem', letterSpacing: '-2px' }}>1,500k</h3>
                      <span className="ms-2 opacity-50 h5 mb-0">TZS</span>
                    </div>
                    <div className="mt-3 py-2 px-3 rounded-1 d-inline-block" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                      <span className="small text-light opacity-75">*Subject to logical complexity & scope</span>
                    </div>
                  </div>

                  <div className="d-flex gap-4 mt-5">
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 mb-1 text-secondary-color" style={{ color: 'var(--secondaryColor)' }}>
                        <FaClock size={14} /> <span className="small fw-bold text-uppercase">Lead Time</span>
                      </div>
                      <div className="fw-bold h5">4–12 Weeks</div>
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center gap-2 mb-1" style={{ color: '#00d1b2' }}>
                        <FaShieldAlt size={14} /> <span className="small fw-bold text-uppercase">Escrow Support</span>
                      </div>
                      <div className="fw-bold h5">3 Months</div>
                    </div>
                  </div>
                </div>

                {/* Right Side: Deployment Checklist */}
                <div className="col-lg-7">
                  <div className="p-4 p-lg-5 rounded-1" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                    <h5 className="text-white fw-bold mb-4 d-flex align-items-center gap-2">
                      <span style={{ width: '20px', height: '2px', background: 'var(--secondaryColor)', display: 'inline-block' }}></span>
                      Standard Deployment Package
                    </h5>
                    <div className="row g-3">
                      {includedFeatures.map((item, idx) => (
                        <div key={idx} className="col-12">
                          <div className="d-flex align-items-center gap-3 text-light opacity-75">
                            <FaCheckCircle className="text-success" />
                            <span style={{ fontSize: '0.95rem' }}>{item}</span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-5">
                      <button 
                        onClick={() => navTo({ item: "contact_us" })}
                        className="btn rounded-1 w-100 py-3 fw-bold text-uppercase d-flex align-items-center justify-content-center gap-3"
                        style={{ 
                          background: 'var(--secondaryColor)', 
                          color: 'white',
                          border: 'none',
                          fontSize: '0.85rem',
                          letterSpacing: '1px',
                          transition: 'all 0.3s ease'
                        }}
                      >
                        Initiate Project Discovery <FaArrowRight size={14} />
                      </button>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

      </div>

      <style>{`
        .pricing-card {
            transition: transform 0.5s cubic-bezier(0.165, 0.84, 0.44, 1);
        }
        .pricing-card:hover {
          transform: translateY(-8px);
        }
        .tracking-widest { letter-spacing: 0.15em; }
        .text-secondary-color { color: var(--secondaryColor); }
      `}</style>
    </section>
  );
}